// Text panel on the call screen (owner request 2026-10-03): text the lead on screen, during or after the call, from our
// own numbers. Pick which of their numbers (the business line, a cell that texted us, a mobile from a booking) or type
// another number they gave you; the conversation with that number shows above the composer and refreshes every 10 s.
// A new conversation comes from the number that called them (else the best number of their country). Opened from the
// wrap-up dock's "Text" button or T: the cursor lands in the message box, and typing there never reaches the dialer's
// hotkeys (hotkeys.ts isTypingTarget). The first text to a number that never texted us needs the consent tick.
import './sms.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { QueueItem } from '../../../src/shared/api';
import type { SmsBusinessView, SmsThreadDetail } from '../../../src/shared/api-sms';
import { SMS_US_PENDING_NOTE } from '../../../src/shared/api-sms';
import { errorMessage } from '../api';
import { ErrorBanner, toast } from '../components/ui';
import { Spinner } from '../components/ui-extra';
import { refreshSmsUnread } from '../texts';
import { fmtPhone, toE164Nanp } from './labels';
import { SmsBubbles, SmsComposer } from './SmsConversation';
import { isConsentNeeded, rememberSmsBlock, sendText, sendToast, smsApi, smsAssistFor, usTextPending } from './sms';
import { dispensaryBooking } from './book';

const POLL_MS = 10_000;
const OTHER = 'other';

export function TextPanel(props: { item: QueueItem; calendlySlug: string | null; onClose: () => void }) {
  const b = props.item.business;
  const [view, setView] = useState<SmsBusinessView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState<string>('');          // a textable e164, or OTHER
  const [typed, setTyped] = useState('');
  const [detail, setDetail] = useState<SmsThreadDetail | null>(null);
  const [tick, setTick] = useState(0);              // each refresh of the view also refreshes the open conversation
  const [forceConsent, setForceConsent] = useState<string | null>(null);   // the server asked for the tick on this number
  const seen = useRef(0);

  const loadView = async () => {
    try {
      const v = await smsApi.business(b.id);
      setView(v);
      setError(null);
      setTick((n) => n + 1);
      rememberSmsBlock(b.id, v.blocked);
      // First load: the conversation with unread texts, else the latest one, else the business line.
      setTo((cur) => cur || (v.threads.find((t) => t.unread > 0) ?? v.threads[0])?.theirE164 || v.business.phoneE164);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => {
    setView(null); setDetail(null); setTo(''); setTyped(''); setError(null); setForceConsent(null);
    void loadView();
    const t = setInterval(() => void loadView(), POLL_MS);
    return () => clearInterval(t);
  }, [b.id]);

  const target = to === OTHER ? toE164Nanp(typed) : to || null;
  const thread = target ? view?.threads.find((t) => t.theirE164 === target) ?? null : null;
  const threadKey = thread ? `${thread.id}:${tick}` : '';
  useEffect(() => {
    if (!thread) { setDetail(null); seen.current = 0; return; }
    let live = true;
    smsApi.thread(thread.id).then((d) => {
      if (!live) return;
      setDetail(d);
      if (d.messages.length !== seen.current) void refreshSmsUnread();   // opening it marked the new texts read
      seen.current = d.messages.length;
    }, (e) => { if (live) setError(errorMessage(e)); });
    return () => { live = false; };
  }, [threadKey]);

  const textable = view?.textable ?? [];
  const pickedBlock = to !== OTHER ? textable.find((n) => n.e164 === to)?.blocked ?? null : null;
  const blocked = view?.blocked
    ?? (detail && thread && detail.thread.id === thread.id ? detail.sendBlocked : null)
    ?? pickedBlock
    ?? (!target ? (to === OTHER ? 'Type the 10-digit number they gave you.' : 'Pick a number to text.') : null)
    ?? (!thread && view?.fromProblem ? view.fromProblem : null);
  const fromE164 = thread ? thread.ourE164 : view?.suggestedFrom?.e164 ?? null;
  const fromLine = thread
    ? `From ${fmtPhone(thread.ourE164)} (your conversation with this number).`
    : view?.suggestedFrom ? `From ${fmtPhone(view.suggestedFrom.e164)}: ${view.suggestedFrom.why}.` : null;
  // A new conversation, or one where they never texted and nobody ticked yet, needs the consent tick.
  const consentNeeded = !!target && ((thread ? thread.consentNeeded : true) || forceConsent === target);
  const usPending = !!view && usTextPending(view.usA2pApproved, target, fromE164);
  // AI draft: from the conversation with this number, else (no texts yet) the first text after the call, from the lead.
  const shown = thread && detail?.thread.id === thread.id ? detail.messages : [];
  const assist = smsAssistFor({ threadId: thread?.id ?? null, businessId: b.id, to: target, canSummarize: shown.length > 0 });

  const send = async (body: string, consent: boolean): Promise<boolean> => {
    if (!target) return false;
    try {
      const r = await sendText({ businessId: b.id, to: target, body, ...(consent ? { consent: true } : {}) });
      if (!r) return false;
      const t = sendToast(r, target, usPending);
      toast(t.text, t.kind);
      if (to === OTHER) { setTo(target); setTyped(''); }
      setForceConsent(null);
      await loadView();
      return true;
    } catch (e) {
      if (isConsentNeeded(e)) setForceConsent(target);
      toast(errorMessage(e), 'bad');
      return false;
    }
  };

  return (
    <div class="vd-text-panel">
      <div class="row" style="justify-content:space-between">
        <span class="eyebrow">Text {b.name}</span>
        <button class="btn btn-sm" onClick={props.onClose}>Close <kbd>Esc</kbd></button>
      </div>
      {error ? <ErrorBanner error={error} /> : null}
      {!view && !error ? <Spinner label="Loading texts" /> : null}
      {view?.warning ? <div class="vd-sms-warn" role="note">{view.warning}</div> : null}
      {view ? (
        <div class="vd-text-to" role="radiogroup" aria-label="Text which number">
          {textable.map((n) => {
            const t = view.threads.find((x) => x.theirE164 === n.e164);
            return (
              <label title={n.blocked ?? undefined}>
                <input type="radio" name="sms-to" checked={to === n.e164} onChange={() => setTo(n.e164)} />
                <b>{fmtPhone(n.e164)}</b> <span class="muted">{n.label}</span>
                {t?.unread ? <span class="chip chip-ok">new</span> : null}
                {t?.optedOut ? <span class="chip chip-bad">Replied STOP</span> : n.blocked ? <span class="chip chip-bad">Can't text</span> : null}
              </label>
            );
          })}
          <label>
            <input type="radio" name="sms-to" checked={to === OTHER} onChange={() => setTo(OTHER)} />
            <span>Another number they gave you</span>
            {to === OTHER ? (
              <input type="tel" value={typed} placeholder="(555) 555-0123" aria-label="Their other number" autoFocus
                onInput={(e) => setTyped(e.currentTarget.value)} />
            ) : null}
          </label>
          {fromLine ? <span class="muted" style="font-size:13px">{fromLine}</span> : null}
        </div>
      ) : null}
      {view ? (
        <SmsBubbles messages={shown}
          empty={target ? `No texts with ${fmtPhone(target)} yet.` : 'Pick a number to see the conversation.'} />
      ) : null}
      {view ? (
        <SmsComposer blocked={blocked} country={detail?.country ?? b.country} calendlySlug={props.calendlySlug ?? view.calendlySlug}
          cannabis={dispensaryBooking([b.niche, props.item.campaign.niche])}
          onSend={send} autoFocus focusKey={target} consentNeeded={consentNeeded} notice={usPending ? SMS_US_PENDING_NOTE : null}
          assist={assist} />
      ) : null}
    </div>
  );
}
